import { Sparkles, Zap, Tag, FileText, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const features = [
  {
    icon: Zap,
    title: "AI Extraction",
    description: "Parses signature blocks and email context to capture name, company, title, phone, email, website and address.",
  },
  {
    icon: Tag,
    title: "Smart Categories",
    description: "Auto-assigns your custom categories like Customer, Partner, Supplier or Lead.",
  },
  {
    icon: FileText,
    title: "Smart Notes",
    description: "Generates concise, helpful notes using firmographic context to remember who the contact is and why they matter.",
  },
  {
    icon: Coins,
    title: "Credit-based System",
    description: "Pay only for what you use. No mandatory subscriptions.",
  },
];

const FeatureCard = ({ feature, index }: { feature: typeof features[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: 'scale',
    delay: index * 100,
    threshold: 0.15,
  });

  return (
    <div 
      ref={ref}
      className={`bg-card rounded-xl p-5 md:p-6 shadow-card border border-border hover:shadow-elevated transition-all duration-300 group hover-3d-tilt ${animationClass}`}
    >
      <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors group-hover:animate-scale-bounce">
        <feature.icon className="w-6 h-6 text-primary" />
      </div>
      <h3 className="text-base md:text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors">
        {feature.title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
};

export const Solution = () => {
  const { ref: headerRef, animationClass: headerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: 'slide-up',
    threshold: 0.3,
  });

  const { ref: ctaRef, animationClass: ctaAnimation } = useScrollAnimation<HTMLDivElement>({
    type: 'scale',
    delay: 400,
    threshold: 0.5,
  });

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container px-4">
        <div className="max-w-5xl mx-auto">
          <div 
            ref={headerRef}
            className={`text-center mb-8 md:mb-12 ${headerAnimation}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4 md:mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs md:text-sm font-medium text-primary">You don't need a CRM. You need clarity.</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
              Get CRM intelligence — without the CRM.
            </h2>
            
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Our AI plugin gives you the smart parts of a CRM right inside Outlook: automatic contact extraction, intelligent tagging, and instant research.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
          
          <div 
            ref={ctaRef}
            className={`mt-8 md:mt-12 text-center ${ctaAnimation}`}
          >
            <Button variant="hero" size="lg" asChild className="touch-target touch-active">
              <Link to="/auth">Try with 20 free credits</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
